import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Markdown from "react-markdown";

import rehypeRaw from "rehype-raw";

import { faPlay, faStop } from "@fortawesome/pro-solid-svg-icons";

import { Icon, Modal } from "@/components";

import { isValidUrl } from "@/utils/isValidUrl";

import { ButtonColor } from "@/components/Button/types";

import { IStageContentInfo } from "../../Courses/constants/emptyCourseScenario";
import { FileTypes } from "../../Courses/enums/FileTypes.enum";

interface IContentModal {
	setCurrentContentInfo: (state: IStageContentInfo | null) => void;
	contentInfo: IStageContentInfo | null;
}

export default function ContentModal({ contentInfo, setCurrentContentInfo }: IContentModal) {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`courses.progress.${key}`), [t]);

	const contentRef = useRef<HTMLDivElement>(null);
	const audioRef = useRef<HTMLAudioElement>(null);

	useEffect(() => {
		if (!contentInfo?.autoplay) {
			return;
		}

		requestAnimationFrame(() => {
			if (!contentRef.current) {
				return;
			}

			const mediaElements = contentRef.current.querySelectorAll("audio, video");

			mediaElements.forEach((media) => {
				(media as HTMLMediaElement).play().catch((err) => console.error("Autoplay error:", err));
			});
		});
	}, [contentInfo]);

	const title = useMemo(() => {
		if (!contentInfo) {
			return ts("no-content-available");
		}

		if (contentInfo?.title) {
			return contentInfo?.title;
		}

		switch (contentInfo.contentType?.toUpperCase()) {
			case FileTypes.Text:
				return contentInfo.content?.split("\n")[0]?.slice(0, 40) || ts("no-content-available");
			case FileTypes.Audio:
				return "";
			case FileTypes.Video:
			case FileTypes.Image:
				return contentInfo.content?.split("/")?.pop()?.replace(/^\d+-/, "") ?? "";
			default:
				return ts("unknown-content");
		}
	}, [contentInfo, ts]);

	const [isAudioPlaying, setIsAudioPlaying] = useState(false);

	const renderContent = useCallback(() => {
		if (!contentInfo) {
			return null;
		}

		if (!isValidUrl(contentInfo.content)) {
			return contentInfo.content;
		}

		switch (contentInfo.contentType?.toUpperCase()) {
			case FileTypes.Image:
				return (
					<img
						key={contentInfo.content}
						alt="Content"
						className="block mx-auto w-full h-full max-w-screen-w max-h-[80vh] object-contain"
						src={contentInfo.content}
					/>
				);
			case FileTypes.Video:
				return (
					<video key={contentInfo.content} controls className="block mx-auto w-auto">
						<source src={contentInfo.content} type="video/mp4" />
						{t("basics.not-support-tag")}
					</video>
				);
			case FileTypes.Audio:
				return (
					<>
						{contentInfo?.avatar?.url ? (
							<div
								className="relative group"
								onClick={() => {
									const audio = audioRef?.current;

									if (!audio) {
										return;
									}

									if (!audio.paused) {
										audio.pause();
									} else {
										audio.play();
									}
								}}
							>
								<div className="border-2 border-gray-200 bg-brand-white hover:cursor-pointer rounded-lg px-4 py-2 flex justify-center transition delay-200 opacity-20 group-hover:opacity-100 absolute bottom-0  -translate-y-1/3 right-1/2 translate-x-1/2 z-10 ">
									<Icon icon={isAudioPlaying ? faStop : faPlay} />
								</div>
								<img
									className={`hover:cursor-pointer w-auto h-[200px]`}
									src={`${contentInfo?.avatar?.url}`}
								/>
							</div>
						) : (
							""
						)}
						<audio
							key={contentInfo.content}
							ref={audioRef}
							controls
							className={`${contentInfo.avatar ? "hidden" : "block mx-auto w-full max-w-xl"}`}
							onEnded={() => {
								setIsAudioPlaying(false);
							}}
							onPause={() => {
								setIsAudioPlaying(false);
							}}
							onPlaying={() => {
								setIsAudioPlaying(true);
							}}
						>
							<source src={contentInfo.content} type="audio/mpeg" />
							{t("basics.not-support-tag")}
						</audio>
					</>
				);
			default:
				return <p>{ts("unsupported-content-type")}</p>;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAudioPlaying, contentInfo]);

	const size = useMemo(() => {
		switch (contentInfo?.contentType?.toUpperCase()) {
			case FileTypes.Image:
				return "lg";
			case FileTypes.Audio:
				return "xs";
			case FileTypes.Video:
				return "lg";
			case FileTypes.Spoiler:
				return "xs";
			default:
				return "xs";
		}
	}, [contentInfo]);

	return (
		<Modal
			dialogContentClassName="!px-4 max-h-[80vh] overflow-auto mr-1 custom-scrollbar"
			handleSave={() => setCurrentContentInfo(null)}
			isLoading={false}
			isOpened={!!contentInfo}
			saveButtonContainerClassName="!w-full"
			showActions={contentInfo?.contentType === FileTypes.Spoiler}
			size={size}
			submitButtonColor={ButtonColor.ACTION}
			submitButtonText={ts("i-see")}
			title={""}
		>
			<div ref={contentRef} className="flex flex-col h-full">
				<div className="flex-grow w-full mb-4 flex justify-between">
					<h3 className="mb-1">{title}</h3>
				</div>
				{contentInfo?.content && !isValidUrl(contentInfo?.content) ? (
					<>
						<Markdown key={contentInfo.content} rehypePlugins={[rehypeRaw]}>
							{renderContent() as string}
						</Markdown>
					</>
				) : (
					<div className="flex-1 flex items-center justify-center">{renderContent()}</div>
				)}
			</div>
		</Modal>
	);
}
